import { Copy, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DistributionWidget } from "@/components/widget/DistributionWidget";
import { useDistributionWidget } from "@/hooks/embed/useDistributionWidget";
import type { DistributionWidgetEmbedProps } from "@/types/distribution-widgets";

export const DistributionWidgetEmbed = ({ genie, kind }: DistributionWidgetEmbedProps) => {
  const { config, embedCode, copyCode, testWidget } = useDistributionWidget({ genie, kind });
  const phone = kind === "phone";

  return (
    <div className="grid items-start gap-6 p-4 xl:grid-cols-2">
      <div className="min-w-0 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700">Live preview</h3>
        <div className="rounded-2xl border border-genie-border bg-genie-background px-3 py-6 sm:px-6 sm:py-8">
          <DistributionWidget config={config} />
        </div>
      </div>
      <div className="min-w-0 space-y-5 rounded-2xl border border-genie-border bg-white p-5 sm:p-6">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Add to your website</h3>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">{phone ? "Your Genie’s phone number, one tap away." : "Give visitors an easy way to open your Genie on their phone."}</p>
        </div>
        <div className="space-y-2 border-y border-genie-border py-5">
          <p className="text-sm font-semibold text-gray-700">{phone ? "Calls go to" : "Scans open"}</p>
          <p className="break-words text-sm text-gray-600">{phone ? config.phoneNumber || "No phone number assigned yet" : config.href || "Your Genie’s live page"}</p>
        </div>
        {config.unavailableReason ? (
          <p role="status" className="text-sm leading-relaxed text-gray-600">{config.unavailableReason}</p>
        ) : (
          <>
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Try it first</h3>
              <p className="text-sm leading-relaxed text-gray-600">{phone ? "Open a test page and tap Call now, or dial the number on your phone." : "Open a test page and scan the code with your phone camera."}</p>
              <Button onClick={testWidget} variant="outline" className="min-h-11 gap-2"><ExternalLink className="h-4 w-4" aria-hidden="true" /> Test widget</Button>
              {!phone && <p className="text-xs leading-relaxed text-gray-500">Scans open the website address shown above, including when you test from this computer.</p>}
            </div>
            <p className="text-sm leading-relaxed text-gray-600">Paste this into an HTML block where you want the widget to appear.</p>
            <Button onClick={copyCode} className="min-h-11 gap-2 bg-gradient-to-r from-genie-primary to-genie-secondary px-6 text-white shadow-lg shadow-genie-primary/20 hover:opacity-90">
              <Copy className="h-4 w-4" aria-hidden="true" /> Copy widget code
            </Button>
            <details className="rounded-xl border border-genie-border">
              <summary className="min-h-11 cursor-pointer px-4 py-3 text-sm font-medium text-gray-700">View HTML code</summary>
              <pre className="max-h-64 overflow-auto rounded-b-xl bg-gray-900 p-4 text-xs text-gray-100"><code>{embedCode}</code></pre>
            </details>
            <p className="text-xs leading-relaxed text-gray-500">{phone ? "Uses the number currently assigned to this Genie." : "Includes the QR code directly, with no extra image upload."} Copy fresh code after changing your {phone ? "number or branding" : "Genie’s page address or branding"}.</p>
          </>
        )}
      </div>
    </div>
  );
};
